import { useId } from 'react';

import { useT } from '@/i18n';

import styles from './how.module.css';

/**
 * The picture in “How Bridge works”: a calm schematic on a Cloud sheet with a
 * dot grid. Your device encrypts (1), only ciphertext flows to the server (2)
 * and on to their device (4), the key goes over the server inside the link
 * (3), their device decrypts (5). White devices with slate hairlines; the data
 * is Passwork blue, the key green; the server's badge is a neutral crossed
 * key. Drawn twice — across for wide screens, and for phones as a list of the
 * four steps with a small picture each — so the words stay readable at any width.
 *
 * The layout is measured, not eyeballed: the two data lines are the same
 * length (135 units across, 72 down) with the packets in their middles, every
 * step number sits on what it numbers (1 and 5 lead their captions, 2 and 4
 * head their packets, 3 rides the key's route), and each caption is centred
 * under its device. Label widths come from Passwork at these sizes.

 */

const SECRET = 'hunter2';
const CIPHER = '9f#Kq2…';

type Point = readonly [number, number];

/** Widths of the labels in Passwork at the sizes used here, measured in the browser (before the font loads the system font is a few percent narrower). */
const WIDTH = {
  yourDevice: 73.6,
  theirDevice: 76.6,
  upload: 34.4,
  download: 49.3,
  cipher: 45.2,
  key: 87.0,
  aes: 67.6,
  decrypted: 41.1,
  legendData: 72.7,
  legendKey: 154.3,
} as const;

const STEP_R = 7.5;

function Step({ at, n }: { readonly at: Point; readonly n: number }): React.JSX.Element {
  return (
    <g>
      <circle className={styles.step} cx={at[0]} cy={at[1]} r={STEP_R} />
      <text className={styles.stepText} x={at[0]} y={at[1] + 3.3} textAnchor="middle">
        {n}
      </text>
    </g>
  );
}

function LockGlyph({ at, open = false, className }: { readonly at: Point; readonly open?: boolean; readonly className: string | undefined }): React.JSX.Element {
  return (
    <g transform={`translate(${String(at[0])} ${String(at[1])})`} className={className}>
      <rect x="0" y="4.5" width="9" height="7" rx="1.8" />
      <path d={open ? 'M2 4.5V3.2a2.5 2.5 0 0 1 4.9-.7' : 'M2 4.5V3.2a2.5 2.5 0 0 1 5 0v1.3'} />
    </g>
  );
}

/** A key, 14 × 9, its ring on the left; `at` is the top-left. */
function KeyGlyph({ at, className }: { readonly at: Point; readonly className: string | undefined }): React.JSX.Element {
  return (
    <g transform={`translate(${String(at[0])} ${String(at[1])})`} className={className}>
      <circle cx="3.5" cy="4.5" r="3" />
      <path d="M6.5 4.5h7M11 4.5v2.6M13.5 4.5v2" />
    </g>
  );
}

/** An encrypted packet riding on a data line, centred on `at`: a lock and the ciphertext. */
const PACKET_W = 86;
function Packet({ at }: { readonly at: Point }): React.JSX.Element {
  const [cx, cy] = at;
  const start = cx - (9 + 6 + WIDTH.cipher) / 2;
  return (
    <g>
      <rect className={styles.packet} x={cx - PACKET_W / 2} y={cy - 12} width={PACKET_W} height="24" rx="12" />
      <LockGlyph at={[start, cy - 8]} className={styles.packetGlyph} />
      <text className={styles.packetText} x={start + 15} y={cy + 3.8}>
        {CIPHER}
      </text>
    </g>
  );
}

/** A step number and a word, the pair centred on `cx` (or starting at `x` when `align` is 'start'). */
function Tag({ at, n, text, width, align = 'middle' }: { readonly at: Point; readonly n: number; readonly text: string; readonly width: number; readonly align?: 'middle' | 'start' }): React.JSX.Element {
  const total = STEP_R * 2 + 6 + width;
  const left = align === 'middle' ? at[0] - total / 2 : at[0];
  return (
    <g>
      <Step at={[left + STEP_R, at[1]]} n={n} />
      <text className={styles.tagText} x={left + STEP_R * 2 + 6} y={at[1] + 4}>
        {text}
      </text>
    </g>
  );
}

/** Your device: a browser window with the secret typed in; `at` is its top-left (128 × 100). */
function Browser({ at }: { readonly at: Point }): React.JSX.Element {
  const [x, y] = at;
  const w = 128;
  return (
    <g>
      <rect className={styles.body} x={x} y={y} width={w} height="100" rx="10" />
      <path className={styles.chrome} d={`M${String(x + 0.5)} ${String(y + 18)}V${String(y + 10)}a9.5 9.5 0 0 1 9.5-9.5H${String(x + w - 10)}a9.5 9.5 0 0 1 9.5 9.5V${String(y + 18)}z`} />
      <path className={styles.hairline} d={`M${String(x)} ${String(y + 18)}h${String(w)}`} />
      {[12, 19, 26].map((dx) => (
        <circle key={dx} className={styles.winDot} cx={x + dx} cy={y + 9} r="2.2" />
      ))}
      <rect className={styles.address} x={x + 36} y={y + 5} width={w - 46} height="8" rx="4" />
      <rect className={styles.field} x={x + 16} y={y + 34} width="96" height="24" rx="7" />
      <text className={styles.secret} x={x + 64} y={y + 50.5} textAnchor="middle">
        {SECRET}
      </text>
      <rect className={styles.cipherTag} x={x + 64 - (WIDTH.aes + 20) / 2} y={y + 68} width={WIDTH.aes + 20} height="16" rx="8" />
      <text className={styles.cipherTagText} x={x + 64} y={y + 79.5} textAnchor="middle">
        AES-256-GCM
      </text>
    </g>
  );
}

/** The server: three units holding ciphertext, a neutral crossed-out key on its corner; `at` is the top-left (104 × 108). */
function Server({ at }: { readonly at: Point }): React.JSX.Element {
  const [x, y] = at;
  return (
    <g>
      <rect className={styles.body} x={x} y={y} width="104" height="108" rx="12" />
      {[0, 1, 2].map((row) => {
        const top = y + 12 + row * 30;
        return (
          <g key={row}>
            <rect className={styles.unit} x={x + 10} y={top} width="84" height="24" rx="6" />
            <circle className={styles.led} cx={x + 21} cy={top + 12} r="2.4" />
            {row === 1 ? (
              <text className={styles.unitText} x={x + 31} y={top + 16}>
                {CIPHER}
              </text>
            ) : (
              [0, 1, 2, 3].map((v) => <path key={v} className={styles.vent} d={`M${String(x + 60 + v * 7)} ${String(top + 8)}v8`} />)
            )}
          </g>
        );
      })}
      {/* no key here */}
      <circle className={styles.noKey} cx={x + 104} cy={y} r="11" />
      <KeyGlyph at={[x + 97, y - 4.5]} className={styles.noKeyGlyph} />
      <path className={styles.noKeyStrike} d={`M${String(x + 97.5)} ${String(y + 6.5)}l13-13`} />
    </g>
  );
}

/** Their phone: the secret back in clear; `at` is the top-left of the body (68 × 124). */
function Phone({ at }: { readonly at: Point }): React.JSX.Element {
  const t = useT();
  const [x, y] = at;
  const tagW = WIDTH.decrypted + 14;
  return (
    <g>
      <rect className={styles.body} x={x} y={y} width="68" height="124" rx="14" />
      <rect className={styles.display} x={x + 5} y={y + 5} width="58" height="114" rx="10" />
      <rect className={styles.island} x={x + 25} y={y + 11} width="18" height="5" rx="2.5" />
      <LockGlyph at={[x + 29.5, y + 33]} open className={styles.openGlyph} />
      <text className={[styles.secret, styles.secretSmall].join(' ')} x={x + 34} y={y + 67} textAnchor="middle">
        {SECRET}
      </text>
      <rect className={styles.openTag} x={x + 34 - tagW / 2} y={y + 77} width={tagW} height="15" rx="7.5" />
      <text className={styles.openTagText} x={x + 34} y={y + 87.5} textAnchor="middle">
        {t('howDiagram.decrypted')}
      </text>
    </g>
  );
}

/** A device's caption, centred on `cx`: the step number and the title as one centred group, then the lines. */
function Caption({ at, step, title, titleWidth = 0, lines }: { readonly at: Point; readonly step?: number; readonly title: string; readonly titleWidth?: number; readonly lines: readonly { readonly text: string; readonly strong?: boolean }[] }): React.JSX.Element {
  const [cx, y] = at;
  const left = step === undefined ? 0 : cx - (STEP_R * 2 + 7 + titleWidth) / 2;
  return (
    <g>
      {step === undefined ? (
        <text className={styles.capTitle} x={cx} y={y} textAnchor="middle">
          {title}
        </text>
      ) : (
        <>
          <Step at={[left + STEP_R, y - 5]} n={step} />
          <text className={styles.capTitle} x={left + STEP_R * 2 + 7} y={y}>
            {title}
          </text>
        </>
      )}
      {lines.map((line, index) => (
        <text key={line.text} className={line.strong ? styles.capStrong : styles.capText} x={cx} y={y + 19 + index * 16} textAnchor="middle">
          {line.text}
        </text>
      ))}
    </g>
  );
}

/** A step of the narrow picture: its number and name, two short lines under them; `at` is the top-left. */
function Row({ at, n, title, lines }: { readonly at: Point; readonly n: number; readonly title: string; readonly lines: readonly { readonly text: string; readonly strong?: boolean }[] }): React.JSX.Element {
  const [x, y] = at;
  return (
    <g>
      <Step at={[x + STEP_R, y + 18]} n={n} />
      <text className={styles.rowTitle} x={x + STEP_R * 2 + 7} y={y + 23}>
        {title}
      </text>
      {lines.map((line, index) => (
        <text key={line.text} className={line.strong ? styles.rowStrong : styles.rowText} x={x} y={y + 44 + index * 17}>
          {line.text}
        </text>
      ))}
    </g>
  );
}

/** A connector: the data flowing along as small dots, and an open chevron at the end. */
function Arrow({ from, to }: { readonly from: Point; readonly to: Point }): React.JSX.Element {
  const [x1, y1] = from;
  const horizontal = y1 === to[1];
  const dir = horizontal ? Math.sign(to[0] - x1) : Math.sign(to[1] - y1);
  // the chevron's tip stops 3 units short of the device, so its stroke never touches the device's outline
  const x2 = horizontal ? to[0] - 3 * dir : to[0];
  const y2 = horizontal ? to[1] : to[1] - 3 * dir;
  const head = horizontal
    ? `M${String(x2 - 6 * dir)} ${String(y2 - 5)}L${String(x2)} ${String(y2)}L${String(x2 - 6 * dir)} ${String(y2 + 5)}`
    : `M${String(x2 - 5)} ${String(y2 - 6 * dir)}L${String(x2)} ${String(y2)}L${String(x2 + 5)} ${String(y2 - 6 * dir)}`;
  const end: Point = horizontal ? [x2 - 3 * dir, y2] : [x2, y2 - 3 * dir];
  const d = `M${String(x1)} ${String(y1)}L${String(end[0])} ${String(end[1])}`;
  return (
    <g>
      <path className={styles.flow} d={d} />
      <path className={styles.dataHead} d={head} />
    </g>
  );
}

/** The key's route round the server: green dots moving towards their device. */
function KeyRoute({ d, head }: { readonly d: string; readonly head: string }): React.JSX.Element {
  return (
    <g>
      <path className={styles.key} d={d} />
      <path className={styles.keyHead} d={head} />
    </g>
  );
}

/** The legend under the wide picture, centred on `cx`. */
function Legend({ at }: { readonly at: Point }): React.JSX.Element {
  const t = useT();
  const [cx, y] = at;
  const total = 22 + 8 + WIDTH.legendData + 32 + 22 + 8 + WIDTH.legendKey;
  const x = cx - total / 2;
  const keyX = x + 22 + 8 + WIDTH.legendData + 32;
  return (
    <g>
      <path className={styles.flow} d={`M${String(x)} ${String(y)}h22`} />
      <text className={styles.legendText} x={x + 30} y={y + 4}>
        {t('howDiagram.legendData')}
      </text>
      <path className={styles.key} d={`M${String(keyX)} ${String(y)}h22`} />
      <text className={styles.legendText} x={keyX + 30} y={y + 4}>
        {t('howDiagram.legendKey')}
      </text>
    </g>
  );
}

function Dots({ id }: { readonly id: string }): React.JSX.Element {
  return (
    <defs>
      <pattern id={id} width="16" height="16" patternUnits="userSpaceOnUse">
        <circle className={styles.gridDot} cx="8" cy="8" r="1" />
      </pattern>
    </defs>
  );
}

export function HowDiagram(): React.JSX.Element {
  const t = useT();
  const serverLines = [{ text: t('howDiagram.serverHolds') }, { text: t('howDiagram.serverNever'), strong: true }];
  const id = useId().replace(/:/g, '');
  const wideDots = `${id}-wide`;
  const narrowDots = `${id}-narrow`;
  const keyTagW = 14 + 14 + 6 + WIDTH.key + 14;
  return (
    <figure className={styles.figure}>
      {/* Wide: left to right. Devices centred at 119, 370 and 591 on the line y = 150; both data lines 135 long. */}
      <svg className={styles.wide} viewBox="0 0 680 324" aria-hidden="true" focusable="false">
        <Dots id={wideDots} />
        <rect width="680" height="324" fill={`url(#${wideDots})`} />

        {/* 3: the key goes over the server, inside the link */}
        <KeyRoute d="M119 100V66a10 10 0 0 1 10-10H581a10 10 0 0 1 10 10V84" head="M586 79l5 6 5-6" />
        <g>
          <rect className={styles.keyTag} x={370 - keyTagW / 2} y="43" width={keyTagW} height="26" rx="13" />
          <KeyGlyph at={[370 - keyTagW / 2 + 14, 51.5]} className={styles.keyGlyph} />
          <text className={styles.keyTagText} x={370 - keyTagW / 2 + 34} y={60}>
            {t('howDiagram.keyTag')}
          </text>
        </g>
        <Step at={[212, 56]} n={3} />

        <Browser at={[55, 100]} />
        <Server at={[318, 96]} />
        <Phone at={[557, 88]} />

        {/* 2 and 4: only ciphertext travels through the server */}
        <Arrow from={[183, 150]} to={[318, 150]} />
        <Packet at={[250.5, 150]} />
        <Tag at={[250.5, 122]} n={2} text={t('howDiagram.upload')} width={WIDTH.upload} />
        <Arrow from={[422, 150]} to={[557, 150]} />
        <Packet at={[489.5, 150]} />
        <Tag at={[489.5, 122]} n={4} text={t('howDiagram.download')} width={WIDTH.download} />

        <Caption at={[119, 240]} step={1} title={t('howDiagram.yourDevice')} titleWidth={WIDTH.yourDevice} lines={[{ text: t('howDiagram.yourDeviceWide') }]} />
        <Caption at={[370, 240]} title={t('howDiagram.server')} lines={serverLines} />
        <Caption at={[591, 240]} step={5} title={t('howDiagram.theirDevice')} titleWidth={WIDTH.theirDevice} lines={[{ text: t('howDiagram.theirDeviceWide') }]} />

        <Legend at={[340, 302]} />
      </svg>

      {/* Narrow: the steps as a list — a small picture on the left, its words on the right — joined by the data's
          blue dots and the key's green ones, so a phone shows one clear column instead of a tall drawing */}
      <svg className={styles.narrow} viewBox="0 0 320 432" aria-hidden="true" focusable="false">
        <Dots id={narrowDots} />
        <rect width="320" height="432" fill={`url(#${narrowDots})`} />

        <path className={styles.flow} d="M56 88V124" />
        <path className={styles.key} d="M56 194V230" />
        <path className={styles.flow} d="M56 300V336" />

        {/* 1: your device, the secret typed in and locked */}
        <rect className={styles.body} x="20" y="18" width="72" height="70" rx="14" />
        <rect className={styles.field} x="30" y="34" width="52" height="18" rx="5" />
        <text className={[styles.secret, styles.secretSmall].join(' ')} x="56" y="47" textAnchor="middle">
          {SECRET}
        </text>
        <rect className={styles.cipherTag} x="29" y="59" width="54" height="14" rx="7" />
        <text className={[styles.cipherTagText, styles.cipherTagSmall].join(' ')} x="56" y="69" textAnchor="middle">
          AES-256
        </text>
        <Row at={[108, 18]} n={1} title={t('howDiagram.yourDevice')} lines={[{ text: t('howDiagram.row1a') }, { text: t('howDiagram.row1b') }]} />

        {/* 2: the server holds the ciphertext */}
        <rect className={styles.body} x="20" y="124" width="72" height="70" rx="14" />
        {[0, 1, 2].map((row) => (
          <rect key={row} className={styles.unit} x="30" y={134 + row * 17} width="52" height="14" rx="4" />
        ))}
        <text className={[styles.packetText, styles.packetSmall].join(' ')} x="56" y="161.5" textAnchor="middle">
          {CIPHER}
        </text>
        <Row at={[108, 124]} n={2} title={t('howDiagram.server')} lines={serverLines} />

        {/* 3: the key goes round the server, inside the link */}
        <rect className={styles.keyTag} x="20" y="230" width="72" height="70" rx="14" />
        <KeyGlyph at={[49, 244]} className={styles.keyGlyph} />
        <text className={styles.keyTagText} x="56" y="275" textAnchor="middle">
          #key
        </text>
        <text className={[styles.keyTagText, styles.keyTagSmall].join(' ')} x="56" y="289" textAnchor="middle">
          {t('howDiagram.inLink')}
        </text>
        <Row at={[108, 230]} n={3} title={t('howDiagram.theKey')} lines={[{ text: t('howDiagram.row3a') }, { text: t('howDiagram.row3b'), strong: true }]} />

        {/* 4: their device downloads the ciphertext and decrypts it */}
        <rect className={styles.body} x="20" y="336" width="72" height="70" rx="14" />
        <rect className={styles.field} x="30" y="352" width="52" height="18" rx="5" />
        <text className={[styles.secret, styles.secretSmall].join(' ')} x="56" y="365" textAnchor="middle">
          {SECRET}
        </text>
        <rect className={styles.openTag} x="31" y="377" width="50" height="14" rx="7" />
        <text className={styles.openTagText} x="56" y="387" textAnchor="middle">
          {t('howDiagram.decrypted')}
        </text>
        <Row at={[108, 336]} n={4} title={t('howDiagram.theirDevice')} lines={[{ text: t('howDiagram.row4a') }, { text: t('howDiagram.row4b') }]} />
      </svg>
      <p className={styles.legendNarrow} aria-hidden="true">
        <span className={styles.legendItem}>
          <span className={styles.legendData} />
          {t('howDiagram.legendData')}
        </span>
        <span className={styles.legendItem}>
          <span className={styles.legendKey} />
          {t('howDiagram.legendKey')}
        </span>
      </p>
      <figcaption className="visually-hidden">
        {t('howDiagram.caption')}
      </figcaption>
    </figure>
  );
}
